import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Bell, Loader2, RefreshCw, FileCheck, PackageCheck, AlertTriangle,
  Star, Zap, ClipboardList, Check,
} from 'lucide-react';

const API = '/api';
const POLL_MS = 60000;
// A focus event this soon after the last load does not reload: each load is ~1 s of
// server work, and focus fires on every return to the tab.
const FOCUS_MIN_GAP_MS = 30000;

// type -> icon + accent (high-importance types lean red/amber)
const TYPE_META = {
  agreement:   { Icon: FileCheck,     cls: 'text-yellow-300' },
  return:      { Icon: PackageCheck,  cls: 'text-red-400' },
  suppression: { Icon: AlertTriangle, cls: 'text-red-400' },
  review:      { Icon: Star,          cls: 'text-amber-300' },
  content:     { Icon: Zap,           cls: 'text-hg-green' },
  checkin:     { Icon: ClipboardList, cls: 'text-sky-300' },
};

const relTime = (s) => {
  try {
    const d = new Date(s); const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch { return ''; }
};

export default function NotificationBell({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inFlight = useRef(false);
  const lastLoadAt = useRef(0);

  // One request at a time: a poll, a focus and a manual refresh that coincide share it.
  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    lastLoadAt.current = Date.now();
    try {
      setLoading(true);
      const r = await axios.get(`${API}/notifications`);
      setData(r.data || { notifications: [], unread_count: 0, total_count: 0 });
    } catch {
      /* silent — bell must never break the header */
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  // mount + poll + refresh on tab focus (unless it loaded moments ago)
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => {
      if (Date.now() - lastLoadAt.current >= FOCUS_MIN_GAP_MS) load();
    };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [load]);

  // close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markSeen = async (key) => {
    try { await axios.post(`${API}/notifications/seen`, { key }); } catch { /* noop */ }
  };

  const handleRow = async (n) => {
    setOpen(false);
    if (n.unread) {
      // optimistic: drop the unread flag locally, then persist
      setData((d) => ({
        ...d,
        unread_count: Math.max(0, d.unread_count - 1),
        notifications: d.notifications.map((x) => (x.key === n.key ? { ...x, unread: false } : x)),
      }));
      await markSeen(n.key);
    }
    if (onNavigate && n.module) onNavigate(n.module);
  };

  const markAll = async () => {
    setData((d) => ({ ...d, unread_count: 0, notifications: d.notifications.map((x) => ({ ...x, unread: false })) }));
    try { await axios.post(`${API}/notifications/seen`, { all: true }); } catch { /* noop */ }
  };

  const unread = data.unread_count || 0;
  const items = data.notifications || [];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 text-hg-text-dim hover:text-hg-text transition-colors"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={18} strokeWidth={1.5} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-sans font-medium leading-[16px] text-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-[340px] max-w-[92vw] bg-hg-bg border border-hg-border rounded-md shadow-2xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-hg-border">
            <div className="text-xs font-sans text-hg-text uppercase tracking-[0.18em]">
              Notifications{items.length ? ` · ${items.length}` : ''}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={load}
                className="p-1 text-hg-text-dim hover:text-hg-text transition-colors"
                title="Refresh"
              >
                {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              </button>
              {unread > 0 && (
                <button
                  data-testid="notif-mark-all"
                  onClick={markAll}
                  className="flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green transition-colors"
                  title="Mark all read"
                >
                  <Check size={12} /> Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm font-sans text-hg-text-dim">
                {loading ? 'Loading…' : "You're all caught up."}
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || { Icon: Bell, cls: 'text-hg-text-dim' };
                const Icon = meta.Icon;
                return (
                  <button
                    key={n.key}
                    data-testid={`notif-row-${n.type}`}
                    onClick={() => handleRow(n)}
                    className={`w-full text-left flex items-start gap-3 px-3 py-2.5 border-b border-hg-border/60 transition-colors hover:bg-hg-surface ${
                      n.unread ? 'bg-hg-surface/40' : ''
                    }`}
                  >
                    <div className="mt-0.5 flex-shrink-0 relative">
                      <Icon size={16} strokeWidth={1.5} className={meta.cls} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-sans text-hg-text leading-snug break-words flex-1">
                          {n.title}
                        </span>
                        {n.unread && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-1" />}
                      </div>
                      {n.subtitle && (
                        <div className="text-[11px] font-sans text-hg-text-dim mt-0.5 break-words">{n.subtitle}</div>
                      )}
                      <div className="text-[10px] font-sans text-hg-text-dim/70 mt-0.5">{relTime(n.created_at)}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
